import { useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { hasPlaceholder, isLiteralImageUrl } from '@olagg/validation-schemas/articleCta.schemas'
import { CONTENT_LANGS, renditionFor } from './constants'

export type MeasureStatus = 'empty' | 'placeholder' | 'measuring' | 'measured' | 'unreachable'

export type Measurement = {
  status: MeasureStatus
  width?: number
  height?: number
}

/** Hosts propios: cualquier otro es un tercero y se avisa (hoy 189 artículos sirven el ad desde i.ibb.co). */
const TRUSTED_HOST = /(^|\.)olagg\.io$|(^|\.)newsroom\.gg$/i

const DEBOUNCE_MS = 400

/**
 * `naturalWidth`/`naturalHeight` se leen sin problema en imágenes de otro
 * dominio: sólo leer *píxeles* por canvas exige CORS. No poner `crossOrigin`
 * acá convierte una carga que funciona en una que falla.
 */
const measure = (url: string) =>
  new Promise<{ width: number, height: number }>((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = () => reject(new Error('unreachable'))
    img.src = url
  })

const NON_MEASURABLE = /\.(svg|gif)(\?|#|$)/i

/**
 * Mide el arte cuando se pega la URL (R6.1). Nadie tipea el ancho ni el alto: el
 * formulario los llena y quedan de sólo lectura.
 *
 * Hay un solo par de dimensiones para los tres idiomas, así que se guarda la
 * medición más chica: es la que decide si el arte aguanta la caja del slot.
 */
export const useArtMeasurement = () => {
  const { watch, setValue, setError, clearErrors } = useFormContext()
  const slot = watch('slot')
  const urls = CONTENT_LANGS.map(lang => watch(`content.${lang}.imageUrl`) as string | undefined)
  const [measurements, setMeasurements] = useState<Record<string, Measurement>>({})
  // La última URL medida por idioma: evita volver a descargar la imagen cuando
  // lo único que cambió es el slot (paso 4 de la secuencia de R6.1).
  const measuredUrls = useRef<Record<string, string>>({})

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = []

    CONTENT_LANGS.forEach((lang, index) => {
      const url = (urls[index] || '').trim()
      const field = `content.${lang}.imageUrl`

      if (measuredUrls.current[lang] === url) return

      // Paso 1: la medición anterior deja de valer en cuanto cambia la URL. No se
      // borra el campo a mano: las dimensiones se derivan de las mediciones
      // vigentes (efecto de abajo), así que sacar una medición ya las borra. Es lo
      // que evita guardar las dimensiones de la URL anterior.
      measuredUrls.current[lang] = url
      clearErrors(field)

      if (!url) {
        setMeasurements(prev => ({ ...prev, [lang]: { status: 'empty' } }))
        return
      }

      // La imagen de un placeholder la resuelve el servidor en el request: no
      // hay nada que medir ni que validar (R5.1).
      if (hasPlaceholder(url)) {
        setMeasurements(prev => ({ ...prev, [lang]: { status: 'placeholder' } }))
        return
      }

      if (NON_MEASURABLE.test(url)) {
        setMeasurements(prev => ({ ...prev, [lang]: { status: 'unreachable' } }))
        setError(field, {
          type: 'medible',
          message: 'No se puede usar .svg ni .gif: no se puede medir o no entra en Core Web Vitals'
        })
        return
      }

      setMeasurements(prev => ({ ...prev, [lang]: { status: 'measuring' } }))

      timers.push(setTimeout(() => {
        measure(url)
          .then(({ width, height }) => {
            setMeasurements(prev => ({ ...prev, [lang]: { status: 'measured', width, height } }))
          })
          .catch(() => {
            setMeasurements(prev => ({ ...prev, [lang]: { status: 'unreachable' } }))
            // Paso 3: submit bloqueado. Una URL que no carga no se guarda.
            setError(field, { type: 'unreachable', message: 'La imagen no carga: revisá la URL' })
          })
      }, DEBOUNCE_MS))
    })

    return () => timers.forEach(clearTimeout)
  }, [urls.join('|')])

  /**
   * Paso 2: las dimensiones que se guardan son las de la medición más chica de las
   * vigentes. Se derivan siempre de `measurements`, así que una URL que cambia o
   * que se borra las deja en null sola: no hay forma de guardar el tamaño de una
   * imagen que ya no está en el formulario.
   */
  useEffect(() => {
    const measured = Object.values(measurements).filter(m => m.status === 'measured' && m.width)

    if (!measured.length) {
      setValue('imageWidth', null, { shouldValidate: true })
      setValue('imageHeight', null, { shouldValidate: true })
      return
    }

    const smallest = measured.reduce((min, m) => (m.width! < min.width!) ? m : min)

    setValue('imageWidth', smallest.width, { shouldValidate: true })
    setValue('imageHeight', smallest.height, { shouldValidate: true })
  }, [measurements])

  /**
   * Una fuente más chica que la rendición del slot bloquea el submit, no avisa:
   * Cloudflare no agranda, así que la imagen se serviría blanda. Se recalcula
   * cuando cambia el slot, sin volver a descargar la imagen.
   */
  useEffect(() => {
    const rendition = renditionFor(slot)

    CONTENT_LANGS.forEach((lang, index) => {
      const url = (urls[index] || '').trim()
      const measurement = measurements[lang]
      const field = `content.${lang}.imageUrl`

      if (!isLiteralImageUrl(url) || measurement?.status !== 'measured' || !measurement.width) return

      if (measurement.width < rendition.width) {
        setError(field, {
          type: 'rendition',
          message: `La fuente tiene ${measurement.width}px y el slot sirve ${rendition.width}px: Cloudflare no agranda`
        })
      } else {
        clearErrors(field)
      }
    })
  }, [measurements, slot])

  /**
   * Reglas sobre lo medido (R6.1). El ancho y alto de rendición salen de la
   * tabla del slot, así que cambiar el slot recalcula sin volver a descargar.
   */
  const rulesFor = (lang: string, url?: string | null) => {
    const measurement = measurements[lang]
    const rendition = renditionFor(slot)
    const blocking: string[] = []
    const warnings: string[] = []

    if (!isLiteralImageUrl(url)) return { blocking, warnings, measurement, rendition }

    if (measurement?.status === 'unreachable') {
      blocking.push('La imagen no carga o no se puede medir')
    }

    if (measurement?.status === 'measured' && measurement.width && measurement.height) {
      const { width, height } = measurement

      if (width < rendition.width) {
        blocking.push(`La fuente (${width}px) es más chica que la rendición del slot (${rendition.width}px): Cloudflare no agranda y se va a ver blanda`)
      } else if (width < rendition.minSourceWidth) {
        warnings.push(`En pantallas 2× el helper va a pedir ${rendition.minSourceWidth}px y la fuente tiene ${width}px`)
      }

      const sourceRatio = width / height
      const slotRatio = rendition.width / rendition.height
      const deviation = Math.abs(sourceRatio - slotRatio) / slotRatio

      if (deviation > 0.1) {
        const servedHeight = Math.round(rendition.width / sourceRatio)
        const cropped = Math.abs(servedHeight - rendition.height)
        warnings.push(`La proporción se desvía ${Math.round(deviation * 100)}%: se va a recortar ${cropped}px`)
      }
    }

    try {
      const host = new URL(url!).hostname

      if (!TRUSTED_HOST.test(host)) warnings.push(`${host} no es un host propio`)
    } catch {
      blocking.push('La URL no es válida')
    }

    return { blocking, warnings, measurement, rendition }
  }

  return { measurements, rulesFor, slot }
}
